import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Wallet,
  TrendingDown,
  LineChart,
  Target,
  Lock,
  Settings,
  Shield,
  LogOut,
  Moon,
  Sun,
  Calculator,
  BadgeCheck,
  LifeBuoy,
  Sparkles,
  Menu,
  X,
  SlidersHorizontal,
  Percent,
  GitCompare,
  FolderLock,
  Users,
  ShieldCheck,
  Lightbulb,
  PanelLeftClose,
  PanelLeftOpen,
  Bell,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { usePendingApprovals } from "../../hooks/usePendingApprovals";
import NotificationBell from "../NotificationBell";
import AdvisorClientLayout from "./AdvisorClientLayout";
import logo from "../../assets/access wealth logo icon light.png";
import logo2 from "../../assets/access wealth logo icon.png";

// Grouped for scannability. Settings is reachable from the profile (name) below.
const navSections = [
  {
    items: [
      {
        to: "/dashboard",
        label: "Overview",
        icon: LayoutDashboard,
        testid: "side-overview",
      },
    ],
  },
  {
    label: "Portfolio",
    items: [
      {
        to: "/dashboard/assets",
        label: "Assets",
        icon: Wallet,
        testid: "side-assets",
      },
      {
        to: "/dashboard/liabilities",
        label: "Liabilities",
        icon: TrendingDown,
        testid: "side-liabilities",
      },
      {
        to: "/dashboard/insurance",
        label: "Insurance",
        icon: ShieldCheck,
        testid: "side-insurance",
      },
    ],
  },
  {
    label: "Planning",
    items: [
      {
        to: "/dashboard/projections",
        label: "Projections",
        icon: LineChart,
        testid: "side-projections",
      },
      {
        to: "/dashboard/goals",
        label: "Goals",
        icon: Target,
        testid: "side-goals",
      },
      {
        to: "/dashboard/assumptions",
        label: "Assumptions",
        icon: SlidersHorizontal,
        testid: "side-assumptions",
      },
      {
        to: "/dashboard/taxation",
        label: "Taxation",
        icon: Percent,
        testid: "side-taxation",
      },
      {
        to: "/dashboard/scenarios",
        label: "Scenarios",
        icon: GitCompare,
        testid: "side-scenarios",
      },
    ],
  },
  {
    label: "Family & Documents",
    items: [
      {
        to: "/dashboard/documents",
        label: "Documents",
        icon: FolderLock,
        testid: "side-documents",
      },
      {
        to: "/dashboard/family-sharing",
        label: "Family Sharing",
        icon: Users,
        testid: "side-family-sharing",
      },
      {
        to: "/dashboard/family-vault",
        label: "Family Vault",
        icon: Lock,
        testid: "side-vault",
      },
      {
        to: "/dashboard/access",
        label: "Advisor Access",
        icon: ShieldCheck,
        testid: "side-advisor-access",
      },
    ],
  },
];

function Tooltip({ label, children, side = "top", disabled = false }) {
  const posClass =
    side === "top-right"
      ? "bottom-full right-0 mb-2 w-20 text-center"
      : side === "right"
        ? "left-full top-1/2 -translate-y-1/2 ml-2 w-max max-w-[12rem] text-center"
        : "bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-[12rem] whitespace-nowrap text-center";

  if (disabled) return children;

  return (
    <div className="relative group/tip inline-flex">
      {children}
      <span
        className={`pointer-events-none absolute z-50 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1 text-xs text-slate-700 dark:text-slate-200 opacity-0 shadow-sm transition-opacity group-hover/tip:opacity-100 ${posClass}`}
      >
        {label}
      </span>
    </div>
  );
}

// One sidebar link. Collapses to an icon-only target (with hover tooltip) when
// the sidebar is collapsed.
function SideLink({
  to,
  label,
  icon: Icon,
  end,
  testid,
  collapsed,
  extraClass,
  badge = 0,
}) {
  return (
    <Tooltip label={label} side="top" disabled={!collapsed}>
      <NavLink
        to={to}
        end={end}
        data-testid={testid}
        className={({ isActive }) =>
          `relative flex items-center rounded-lg text-sm font-medium transition-all ${
            collapsed
              ? "justify-center w-9 h-9"
              : "gap-2.5 px-2.5 py-1.5 w-full"
          } ${
            isActive
              ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
              : extraClass ||
                "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
          }`
        }
      >
        <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
        {!collapsed && <span className="truncate flex-1">{label}</span>}
        {badge > 0 &&
          (collapsed ? (
            <span className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white dark:ring-slate-900" />
          ) : (
            <span className="grid h-5 min-w-5 px-1 place-items-center rounded-full bg-rose-500 text-[11px] font-bold text-white">
              {badge}
            </span>
          ))}
      </NavLink>
    </Tooltip>
  );
}

// Routes the personal dashboard chrome vs the advisor's client-view chrome.
// When an advisor has a client selected, the entire shell switches to the
// dedicated advisor experience (separate nav + theme, only shared modules).
export default function DashboardLayout({ children }) {
  const { activeClient } = useAuth();
  if (activeClient)
    return <AdvisorClientLayout>{children}</AdvisorClientLayout>;
  return <UserDashboardLayout>{children}</UserDashboardLayout>;
}

function UserDashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  // Live "needs approval" badge for clients with connected advisors.
  const { count: pendingApprovals } = usePendingApprovals(
    !!user && user.role === "user",
  );
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () =>
      typeof window !== "undefined" &&
      localStorage.getItem("wn_sidebar_collapsed") === "1",
  );

  useEffect(() => {
    localStorage.setItem("wn_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex">
      <aside
        className={`hidden lg:flex flex-col fixed inset-y-0 left-0 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transition-[width] duration-200 ${
          collapsed ? "w-16 overflow-visible" : "w-64 overflow-hidden"
        }`}
      >
        {/* HEADER: logo + collapse toggle */}
        <div
          className={`flex items-center h-16 border-b border-slate-200 dark:border-slate-800 ${
            collapsed ? "justify-center px-0" : "gap-2 px-4"
          }`}
        >
          {!collapsed && (
            <Link to="/" className="flex items-center gap-2 flex-1 min-w-0">
              <div className="grid h-10 w-9 place-items-center overflow-hidden shrink-0">
                <img
                  src={theme === "dark" ? logo : logo2}
                  alt="Access Wealth"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex flex-col leading-tight min-w-0">
                <span className="font-display text-base font-bold text-slate-900 dark:text-white truncate">
                  AccessWealth
                </span>
                <span className="text-[10px] text-slate-500 truncate">
                  Personal Wealth OS
                </span>
              </div>
            </Link>
          )}

          <Tooltip
            label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            side="right"
            disabled={!collapsed}
          >
            <button
              onClick={() => setCollapsed((v) => !v)}
              data-testid="sidebar-collapse-toggle"
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition"
            >
              {collapsed ? (
                <PanelLeftOpen className="h-4 w-4" />
              ) : (
                <PanelLeftClose className="h-4 w-4" />
              )}
            </button>
          </Tooltip>
        </div>

        <div className="flex-1 min-h-0">
          <nav className="h-full flex flex-col min-h-0">
            {/* SCROLLABLE DASHBOARD LINKS */}
            <div
              className={`flex-1 overflow-y-auto py-1.5 space-y-1.5 ${
                collapsed ? "px-1.5 flex flex-col items-center" : "px-2"
              }`}
            >
              {navSections.map((section, si) => (
                <div
                  key={si}
                  className={`space-y-0.5 ${
                    collapsed ? "flex flex-col items-center w-full" : ""
                  }`}
                >
                  {section.label && !collapsed && (
                    <div className="px-2.5 pt-0.5 pb-0.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                      {section.label}
                    </div>
                  )}
                  {section.label && collapsed && si > 0 && (
                    <div className="my-1 h-px w-6 bg-slate-200 dark:bg-slate-800" />
                  )}
                  {section.items.map((it) => (
                    <SideLink
                      key={it.to}
                      to={it.to}
                      label={it.label}
                      icon={it.icon}
                      end={it.to === "/dashboard"}
                      testid={it.testid}
                      collapsed={collapsed}
                      badge={
                        it.to === "/dashboard/access" ? pendingApprovals : 0
                      }
                    />
                  ))}
                </div>
              ))}
            </div>

            {/* FIXED LINKS */}
            <div
              className={`shrink-0 py-1.5 border-t border-slate-200 dark:border-slate-800 space-y-0.5 ${
                collapsed ? "px-1.5 flex flex-col items-center" : "px-2"
              }`}
            >
              <SideLink
                to="/dashboard/insights"
                label="Insights"
                icon={Lightbulb}
                testid="side-insights"
                collapsed={collapsed}
              />

              <SideLink
                to="/calculators"
                label="Calculators"
                icon={Calculator}
                testid="side-calculators"
                collapsed={collapsed}
              />

              {user?.role !== "admin" && (
                <SideLink
                  to="/support"
                  label="Support"
                  icon={LifeBuoy}
                  testid="side-support"
                  collapsed={collapsed}
                />
              )}

              {user?.role === "admin" && (
                <SideLink
                  to="/admin"
                  label="Admin Console"
                  icon={BadgeCheck}
                  testid="side-admin"
                  collapsed={collapsed}
                  extraClass="text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-500/10"
                />
              )}
            </div>
          </nav>
        </div>

        <div
          className={`shrink-0 py-1.5 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 ${
            collapsed ? "px-1.5" : "px-2"
          }`}
        >
          {user?.plan !== "premium" && !collapsed && (
            <div className="mb-3 rounded-xl bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-200/50 dark:border-emerald-500/20 p-3">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-4 w-4 text-emerald-600" />
                <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">
                  Upgrade to Premium
                </span>
              </div>
              <Link
                to="/dashboard/settings"
                data-testid="upgrade-cta-sidebar"
                className="mt-2 inline-block text-xs font-semibold text-emerald-700 dark:text-emerald-300"
              >
                Upgrade →
              </Link>
            </div>
          )}

          {user?.plan !== "premium" && collapsed && (
            <Tooltip label="Upgrade to Premium" side="top">
              <button
                onClick={() => navigate("/dashboard/settings")}
                data-testid="upgrade-cta-sidebar"
                title="Upgrade to Premium"
                className="grid h-10 w-10 mx-auto mb-1 place-items-center rounded-xl text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 transition"
              >
                <Sparkles className="h-4 w-4" />
              </button>
            </Tooltip>
          )}

          <div className="relative">
            <Tooltip label="Account" side="top" disabled={!collapsed}>
              <button
                onClick={() => setShowUserMenu((v) => !v)}
                data-testid="user-menu-trigger"
                aria-haspopup="menu"
                aria-expanded={showUserMenu}
                title="Account"
                className={`flex items-center rounded-xl text-left transition hover:bg-slate-100 dark:hover:bg-slate-800 ${
                  collapsed ? "justify-center w-full p-1" : "gap-3 w-full p-1.5"
                } ${showUserMenu ? "bg-slate-100 dark:bg-slate-800" : ""}`}
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-emerald-100 to-blue-100 dark:from-emerald-500/20 dark:to-blue-500/20 text-emerald-700 dark:text-emerald-300 font-bold text-sm">
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </span>
                {!collapsed && (
                  <span className="flex-1 min-w-0">
                    <span className="block truncate text-sm font-semibold text-slate-900 dark:text-white">
                      {user?.name || "User"}
                    </span>
                    {user?.email && (
                      <span className="block truncate text-[11px] text-slate-500">
                        {user.email}
                      </span>
                    )}
                  </span>
                )}
              </button>
            </Tooltip>

            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowUserMenu(false)}
                />
                <div
                  role="menu"
                  className={`absolute z-50 bottom-full mb-2 w-56 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl p-1.5 ${
                    collapsed ? "left-0" : "left-0 right-0 w-auto"
                  }`}
                >
                  <MenuItem
                    icon={Settings}
                    label="Settings"
                    testid="side-settings"
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate("/dashboard/settings");
                    }}
                  />
                  <MenuItem
                    icon={Bell}
                    label="Notifications"
                    testid="menu-notifications"
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate("/dashboard/notifications");
                    }}
                  />

                  <MenuItem
                    icon={theme === "dark" ? Sun : Moon}
                    label="Change appearance"
                    hint={theme === "dark" ? "Light" : "Dark"}
                    testid="dash-theme-toggle"
                    onClick={toggle}
                  />
                  <div className="my-1 border-t border-slate-200 dark:border-slate-800" />
                  <MenuItem
                    icon={LogOut}
                    label="Logout"
                    testid="dash-logout-btn"
                    danger
                    onClick={() => {
                      setShowUserMenu(false);
                      onLogout();
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </aside>

      <div
        className={`flex-1 transition-[margin] duration-200 ${
          collapsed ? "lg:ml-16" : "lg:ml-64"
        }`}
      >
        {/* Desktop top strip with the notification center */}
        <header className="hidden lg:flex sticky top-0 z-30 h-14 items-center justify-end gap-2 px-10 bg-slate-50/80 dark:bg-slate-950/80 backdrop-blur border-b border-slate-200/70 dark:border-slate-800/70">
          <NotificationBell enabled={!!user} />
        </header>
        <DemoBanner user={user} navigate={navigate} logout={logout} />
        <header className="lg:hidden glass-header sticky top-0 z-30 h-14 flex items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 text-white">
              <Shield className="h-4 w-4" />
            </div>
            <span className="font-display font-bold text-slate-900 dark:text-white">
              AccessWealth
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <NotificationBell enabled={!!user} />
            <button
              onClick={toggle}
              title={
                theme === "dark"
                  ? "Switch to light mode"
                  : "Switch to dark mode"
              }
              className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              {theme === "dark" ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={onLogout}
              title="Log out"
              className="grid h-8 w-8 place-items-center rounded-lg text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-10">{children}</main>
        {/* Bottom nav for mobile */}
        {/* MOBILE MORE MENU */}
        {showMobileMenu && (
          <>
            <div
              className="lg:hidden fixed inset-0 bg-black/40 z-40"
              onClick={() => setShowMobileMenu(false)}
            />

            <div className="lg:hidden fixed inset-x-3 bottom-16 max-h-[70vh] overflow-y-auto z-50 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl">
              <div className="p-2">
                <NavLink
                  to="/dashboard/liabilities"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <TrendingDown className="h-4 w-4" />
                  Liabilities
                </NavLink>

                <NavLink
                  to="/dashboard/insurance"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <ShieldCheck className="h-4 w-4" />
                  Insurance
                </NavLink>

                <NavLink
                  to="/dashboard/projections"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <LineChart className="h-4 w-4" />
                  Projections
                </NavLink>

                <NavLink
                  to="/dashboard/assumptions"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  Assumptions
                </NavLink>

                <NavLink
                  to="/dashboard/taxation"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <Percent className="h-4 w-4" />
                  Taxation
                </NavLink>

                <NavLink
                  to="/dashboard/scenarios"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <GitCompare className="h-4 w-4" />
                  Scenarios
                </NavLink>

                <NavLink
                  to="/dashboard/documents"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <FolderLock className="h-4 w-4" />
                  Documents
                </NavLink>

                <NavLink
                  to="/dashboard/family-sharing"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <Users className="h-4 w-4" />
                  Family Sharing
                </NavLink>

                <NavLink
                  to="/dashboard/family-vault"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <Lock className="h-4 w-4" />
                  Family Vault
                </NavLink>

                <NavLink
                  to="/dashboard/settings"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <Settings className="h-4 w-4" />
                  Settings
                </NavLink>

                <NavLink
                  to="/dashboard/notifications"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <Bell className="h-4 w-4" />
                  Notifications
                </NavLink>

                <div className="my-2 border-t border-slate-200 dark:border-slate-800" />

                <NavLink
                  to="/dashboard/insights"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <Lightbulb className="h-4 w-4" />
                  Insights
                </NavLink>

                <NavLink
                  to="/calculators"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <Calculator className="h-4 w-4" />
                  Calculators
                </NavLink>

                {user?.role !== "admin" && (
                  <NavLink
                    to="/support"
                    onClick={() => setShowMobileMenu(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    <LifeBuoy className="h-4 w-4" />
                    Support
                  </NavLink>
                )}

                {user?.role === "admin" && (
                  <NavLink
                    to="/admin"
                    onClick={() => setShowMobileMenu(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-amber-600"
                  >
                    <BadgeCheck className="h-4 w-4" />
                    Admin Console
                  </NavLink>
                )}

                <button
                  onClick={() => {
                    toggle();
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-left"
                >
                  {theme === "dark" ? (
                    <Sun className="h-4 w-4" />
                  ) : (
                    <Moon className="h-4 w-4" />
                  )}
                  Toggle Theme
                </button>

                <button
                  onClick={onLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-left text-rose-600"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            </div>
          </>
        )}

        {/* MOBILE BOTTOM NAV */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-t border-slate-200 dark:border-slate-800 flex justify-around py-2">
          <NavLink
            to="/dashboard"
            end
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg ${
                isActive
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-slate-500"
              }`
            }
          >
            <LayoutDashboard className="h-4 w-4" />
            <span className="text-[9px] font-medium">Overview</span>
          </NavLink>

          <NavLink
            to="/dashboard/assets"
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg ${
                isActive
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-slate-500"
              }`
            }
          >
            <Wallet className="h-4 w-4" />
            <span className="text-[9px] font-medium">Assets</span>
          </NavLink>

          <NavLink
            to="/dashboard/goals"
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg ${
                isActive
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-slate-500"
              }`
            }
          >
            <Target className="h-4 w-4" />
            <span className="text-[9px] font-medium">Goals</span>
          </NavLink>

          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg text-slate-500"
          >
            {showMobileMenu ? (
              <X className="h-4 w-4" />
            ) : (
              <Menu className="h-4 w-4" />
            )}
            <span className="text-[9px] font-medium">More</span>
          </button>
        </nav>

        <div className="lg:hidden h-16" />
        <div className="lg:hidden h-16" />
      </div>
    </div>
  );
}

function MenuItem({ icon: Icon, label, hint, testid, onClick, danger }) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-left transition ${
        danger
          ? "text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
          : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
      }`}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1">{label}</span>
      {hint && (
        <span className="text-[11px] text-slate-400 dark:text-slate-500">
          {hint}
        </span>
      )}
    </button>
  );
}

function DemoBanner({ user, navigate, logout }) {
  if (!user?.is_demo) return null;

  const createAccount = async () => {
    try {
      await logout();
    } catch (_) {}
    navigate("/register");
  };

  return (
    <div className="relative lg:sticky lg:top-0 z-40 bg-gradient-to-r from-emerald-600 to-blue-600 text-white">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 shrink-0" />
          <p className="text-xs sm:text-sm font-medium truncate">
            You are viewing an interactive AccessWealth demo. Changes made here
            are temporary and will not be saved.
          </p>
        </div>
        <button
          onClick={createAccount}
          data-testid="demo-create-account"
          className="shrink-0 rounded-lg bg-white/95 hover:bg-white text-emerald-700 text-xs font-bold px-3 py-1.5 whitespace-nowrap"
        >
          Create your own account
        </button>
      </div>
    </div>
  );
}
